"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { StaggerGroup, StaggerItem, TextReveal } from "@/components/marketing/animations";

interface FaqItemData {
  question: string;
  answer: string;
}

const FAQ_DATA: FaqItemData[] = [
  {
    question: "C\u2019est quoi Vitrine, exactement ?",
    answer:
      "Tu prends une photo de ton v\u00eatement. Vitrine g\u00e9n\u00e8re 4 photos pro, un titre accrocheur, une description compl\u00e8te et les hashtags. En 30 secondes, ton annonce est pr\u00eate pour Vinted, Depop ou Leboncoin.",
  },
  {
    question: "C\u2019est vraiment gratuit ?",
    answer:
      "Pendant la beta, oui. Z\u00e9ro. Nada. On testera un abonnement \u00e0 9,99 EUR/mois plus tard, mais les beta-testeurs auront un traitement de faveur. Promis.",
  },
  {
    question: "\u00c7a marche avec quoi comme v\u00eatements ?",
    answer:
      "Tout ce qui se vend en seconde main : fringues, chaussures, sacs, accessoires. Si tu peux le photographier, Vitrine peut le sublimer.",
  },
  {
    question:
      "Ma photo est vraiment nulle, \u00e7a marche quand m\u00eame ?",
    answer:
      "C\u2019est litt\u00e9ralement pour \u00e7a qu\u2019on existe. Plus ta photo est nulle, plus l\u2019effet \"wahou\" est impressionnant. On a vu pire que ta photo sur le canap\u00e9, crois-nous.",
  },
  {
    question: "Mes donn\u00e9es sont en s\u00e9curit\u00e9 ?",
    answer:
      "Tes photos sont trait\u00e9es, pas stock\u00e9es \u00e9ternellement. On ne vend rien, on ne partage rien. Tes fringues restent tes fringues (enfin, jusqu\u2019\u00e0 ce que tu les vendes).",
  },
];

function FaqItem({
  item,
  isOpen,
  onToggle,
}: {
  item: FaqItemData;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const answerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  const updateMaxHeight = useCallback(() => {
    if (isOpen && answerRef.current) {
      setMaxHeight(`${answerRef.current.scrollHeight}px`);
    } else {
      setMaxHeight("0px");
    }
  }, [isOpen]);

  useEffect(() => {
    updateMaxHeight();
  }, [updateMaxHeight]);

  return (
    <div style={{ borderBottom: "1px solid rgba(253,246,238,0.1)" }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          background: "none",
          border: "none",
          padding: "24px 0",
          fontFamily: "var(--font-manrope), system-ui, sans-serif",
          fontSize: 17,
          fontWeight: 600,
          color: "var(--color-mkt-cream)",
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {item.question}
        <span
          className="material-symbols-outlined"
          style={{
            transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
            color: "rgba(253,246,238,0.4)",
            transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
          }}
        >
          add
        </span>
      </button>
      <div
        ref={answerRef}
        style={{
          maxHeight,
          overflow: "hidden",
          transition: "max-height 0.4s ease",
        }}
      >
        <p
          style={{
            padding: "0 0 24px",
            fontSize: 15,
            lineHeight: 1.7,
            color: "rgba(253,246,238,0.6)",
          }}
        >
          {item.answer}
        </p>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section
      id="faq"
      style={{
        background: "var(--color-mkt-chocolate)",
        padding: "100px 5vw",
        borderRadius: '32px 32px 0 0',
        marginTop: -32,
        position: 'relative',
        zIndex: 4,
      }}
    >
      <h2
        style={{
          fontFamily: "var(--font-newsreader), Georgia, serif",
          fontStyle: "italic",
          fontSize: "clamp(36px, 5vw, 56px)",
          color: "var(--color-mkt-cream)",
          textAlign: "center",
          marginBottom: 60,
        }}
      >
        <TextReveal text="Questions" stagger={0.06} />
      </h2>

      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        <StaggerGroup stagger={0.07}>
          {FAQ_DATA.map((item, index) => (
            <StaggerItem key={item.question}>
              <FaqItem
                item={item}
                isOpen={openIndex === index}
                onToggle={() => handleToggle(index)}
              />
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}
